export const BLOG_FALLBACK_IMAGE = "/img/card-top.jpg";

/**
 * Resolve a blog post image URL with a safe fallback for NuxtImg.
 * @param {Record<string, string>|null|undefined} image
 * @param {"img_medium"|"img_large"|"img_full"} [size="img_medium"]
 * @returns {string}
 */
export const getBlogImage = (image, size = "img_medium") => {
  return image?.[size] || BLOG_FALLBACK_IMAGE;
};

/** Every blog helper needs the caller's country getter; a missing or misplaced one is a coding error. */
const requireSite = (site, helper) => {
  if (typeof site !== "function") throw new TypeError(`${helper}: pass the country as a getter (site)`);
};

export const useBlogList = async (queryString, site) => {
  requireSite(site, "useBlogList");
  const blogLocale = useBlogLocale(site);
  return useFetchSafely(() => `posts${queryString.value}&lang=${blogLocale.value}&site=${toValue(site)}`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    locale: toValue(site),
    auth: false,
  });
};

export const useBlogLatest = async (site) => {
  requireSite(site, "useBlogLatest");
  const blogLocale = useBlogLocale(site);
  return useFetchSafely(() => `latest?lang=${blogLocale.value}&site=${toValue(site)}`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    locale: toValue(site),
    auth: false,
  });
};

export const useBlogDetail = async (slug, site) => {
  requireSite(site, "useBlogDetail");
  const blogLocale = useBlogLocale(site);
  return useFetchSafely(() => `post`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    params: {
      slug: slug,
      lang: blogLocale.value,
      site: toValue(site),
    },
    locale: toValue(site),
    auth: false,
  });
};

export const useBlogAuthorSlug = async (slug, site) => {
  requireSite(site, "useBlogAuthorSlug");
  return useFetchSafely((authorData) => `author?slug=${slug}&site=${toValue(site)}`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    locale: toValue(site),
    auth: false,
  });
};

export const useCategoryList = async (site) => {
  requireSite(site, "useCategoryList");
  const blogLocale = useBlogLocale(site);
  return useFetchSafely(() => `categories`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    params: {
      hide_empty: true,
      lang: blogLocale.value,
      site: toValue(site),
    },
    locale: toValue(site),
    auth: false,
  });
};

export const useCategoryDetail = async (slug, site) => {
  requireSite(site, "useCategoryDetail");
  const blogLocale = useBlogLocale(site);
  return useFetchSafely(() => `category`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    params: {
      slug: slug,
      lang: blogLocale.value,
      site: toValue(site),
    },
    locale: toValue(site),
    auth: false,
  });
};

export const useTagDetail = async (slug, site) => {
  requireSite(site, "useTagDetail");
  const blogLocale = useBlogLocale(site);
  return useFetchSafely(() => `tag`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    params: {
      slug: slug,
      lang: blogLocale.value,
      site: toValue(site),
    },
    locale: toValue(site),
    auth: false,
  });
};

export const useAuthorList = async (queryString, site) => {
  requireSite(site, "useAuthorList");
  const blogLocale = useBlogLocale(site);
  return useFetchSafely(() => `authors${queryString.value}&lang=${blogLocale.value}&site=${toValue(site)}`, {
    baseURL: useRuntimeConfig().public.apiBlogBase,
    locale: toValue(site),
    auth: false,
  });
};
